import { useRef, useState, useCallback } from 'react';

interface UseCancelConfirmOptions {
  /** Serializable snapshot of the form (e.g. { formData, kpis }). */
  snapshot: unknown;
  /** Capture the baseline only once the screen's data has finished loading. */
  ready?: boolean;
  /** Navigate away / actually cancel. Called when there are no unsaved changes
   *  or after the user confirms discard. */
  onExit: () => void;
}

/**
 * Guards a create/edit form against accidental data loss on Cancel.
 *
 * Captures a baseline snapshot on the first render where `ready` is true, then
 * compares the live snapshot on cancel. If the form is dirty it surfaces a
 * confirmation modal; if untouched it exits immediately.
 */
export function useCancelConfirm({ snapshot, ready = true, onExit }: UseCancelConfirmOptions) {
  const baseline = useRef<string | null>(null);
  const serialized = JSON.stringify(snapshot);

  // One-time baseline capture once data is loaded.
  if (ready && baseline.current === null) {
    baseline.current = serialized;
  }

  const [confirmVisible, setConfirmVisible] = useState(false);

  const isDirty = useCallback(
    () => baseline.current !== null && baseline.current !== JSON.stringify(snapshot),
    [snapshot],
  );

  const requestCancel = useCallback(() => {
    if (isDirty()) {
      setConfirmVisible(true);
    } else {
      onExit();
    }
  }, [isDirty, onExit]);

  const confirmDiscard = useCallback(() => {
    setConfirmVisible(false);
    onExit();
  }, [onExit]);

  const dismissConfirm = useCallback(() => setConfirmVisible(false), []);

  return { requestCancel, confirmVisible, confirmDiscard, dismissConfirm };
}
